import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  // ExternalLink,
  Calendar, 
  MapPin, 
  GraduationCap, 
  Briefcase 
} from 'lucide-react';
import { getProfileConfig, Experience } from '../config/profile';

interface TimelineItem extends Experience {
  icon: React.ReactNode;
}

const Timeline = () => {
  const [timelineData, setTimelineData] = useState<TimelineItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const config = await getProfileConfig();
        // Map experience data to timeline format with icons
        const mappedData: TimelineItem[] = config.experience.map((exp) => {
          const type = exp.type || 'work'; // Default to 'work' if type is not specified
          return {
            ...exp,
            type,
            icon: type === 'education' 
              ? <GraduationCap className="w-6 h-6" />
              : <Briefcase className="w-6 h-6" />
          };
        });
        setTimelineData(mappedData);
      } catch (error) {
        console.error('Failed to load profile config:', error);
      } finally {
        setLoading(false);
      }
    };

    loadConfig();
  }, []);

  if (loading) {
    return (
      <section id="timeline" className="section-padding bg-gray-50 dark:bg-dark-800">
        <div className="container-max">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading timeline...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="timeline" className="section-padding bg-gray-50 dark:bg-dark-800">
      <div className="container-max">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="gradient-text">Professional Journey</span>
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            My educational background and professional experience that shaped my career
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-4 md:left-1/2 transform md:-translate-x-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary-500 to-blue-500"></div>

          {/* Timeline Items */}
          <div className="space-y-12">
            {timelineData.map((item, index) => (
              <motion.div
                key={`${item.type}-${index}`}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`relative flex items-center ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
              >
                {/* Timeline Dot */}
                <div className="absolute left-4 md:left-1/2 transform md:-translate-x-1/2 w-8 h-8 bg-white dark:bg-dark-900 border-4 border-primary-500 rounded-full flex items-center justify-center z-10">
                  <div className="text-primary-600 dark:text-primary-400">
                    {item.icon}
                  </div>
                </div>

                {/* Content Card */}
                <div className={`ml-16 md:ml-0 md:w-5/12 ${
                  index % 2 === 0 ? 'md:mr-auto md:pr-8' : 'md:ml-auto md:pl-8'
                }`}>
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    className="bg-white dark:bg-dark-900 rounded-xl p-6 shadow-lg card-hover"
                  >
                    {/* Type Badge */}
                    <div className="flex items-center gap-2 mb-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        item.type === 'education' 
                          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                          : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      }`}>
                        {item.type === 'education' ? 'Education' : 'Experience'}
                      </span>
                    </div>

                    {/* Title */}
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                      {item.title}
                    </h3>

                    {/* Organization */}
                    <h4 className="text-lg font-medium text-primary-600 dark:text-primary-400 mb-2">
                      {item.organization}
                    </h4>

                    {/* Location and Period */}
                    <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400 mb-4">
                      <div className="flex items-center gap-1">
                        <MapPin size={16} />
                        {item.location}
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar size={16} />
                        {item.period}
                      </div>
                    </div>

                    {/* Description */}
                    <p className="text-gray-600 dark:text-gray-400 mb-4 leading-relaxed">
                      {item.description}
                    </p>

                    {/* Achievements */}
                    <div className="space-y-2">
                      <h5 className="font-medium text-gray-900 dark:text-white">Key Achievements:</h5>
                      <ul className="space-y-1">
                        {item.achievements.map((achievement, achievementIndex) => (
                          <li key={achievementIndex} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                            <span className="w-1.5 h-1.5 bg-primary-500 rounded-full mt-2 flex-shrink-0"></span>
                            {achievement}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          {/* <div className="bg-gradient-to-r from-primary-50 to-blue-50 dark:from-dark-700 dark:to-dark-600 rounded-xl p-8">
            <h3 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white">
              Ready for the Next Chapter
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-2xl mx-auto">
              I'm always looking for new challenges and opportunities to grow. 
              Let's discuss how I can contribute to your next project.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                const element = document.querySelector('#contact');
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-lg font-medium transition-colors duration-200 flex items-center gap-2 mx-auto"
            >
              <ExternalLink size={20} />
              Let's Connect
            </motion.button>
          </div> */}
        </motion.div>
      </div>
    </section>
  );
};

export default Timeline; 